import _get from 'lodash/get';
import i18n from './i18n';

const privilegeUtils = {
  getPrivileges: auth => _get(auth, 'privileges', []),
  isSkinAnalysis: auth =>
    _get(auth, 'privileges', []).includes('S_SKIN_ANALYSIS'),
  isSimulation: auth =>
    _get(auth, 'privileges', []).includes('S_AESTHETIC_SIMULATION'),
  isTechnicalSupport: auth =>
    _get(auth, 'privileges', []).includes('P304_CX_TECHNICAL_SUPPORT'),
  canManageTeamMember: auth =>
    _get(auth, 'privileges', []).includes('P302_CX_TEAM_SETTINGS'),
  canEditTeamMember: auth =>
    _get(auth, 'privileges', []).includes('P305_CX_TEAM_SETTINGS_EDIT'),
  canDeleteTeamMember: auth =>
    _get(auth, 'privileges', []).includes('P306_CX_TEAM_SETTINGS_DELETE'),
  canAccessRoleManagement: auth =>
    _get(auth, 'privileges', []).includes('P307_CX_TEAM_SETTINGS_ROLE_MNGT'),
  canManageReport: auth =>
    _get(auth, 'privileges', []).includes('P102_WM_REPORT'),
  canManageSku: auth =>
    _get(auth, 'privileges', []).includes('P103_SKU_LOOK_MNGT'),
  canManageModules: auth =>
    _get(auth, 'privileges', []).includes('P101_WM_SETTINGS'),
  canManageAPIIntegration: auth =>
    _get(auth, 'privileges', []).includes('P104_API_INTEGRATION'),
  canManageRecommend: auth =>
    _get(auth, 'privileges', []).includes('P108_RECOMMENDATION_RULE'),
  canManageSurveyForm: auth =>
    _get(auth, 'privileges', []).includes('P110_SURVEY_FORM'),
  canUiConfig: auth =>
    _get(auth, 'privileges', []).includes('P105_UI_CONFIGURATION'),
  canManageStore: auth =>
    _get(auth, 'privileges', []).includes('P403_CRM_STORE_MNGT'),
  canManageLicense: auth =>
    _get(auth, 'privileges', []).includes('P106_DEVICE'),
  canManageClient: auth =>
    _get(auth, 'privileges', []).includes('P402_CRM_CUSTOMER_MNGT'),
  canClientVisibleFull: auth =>
    _get(auth, 'privileges', []).includes(
      'P406_CRM_CUSTOMER_MNGT_VISIBLE_FULL'
    ),
  canClientVisibleAccount: auth =>
    _get(auth, 'privileges', []).includes(
      'P407_CRM_CUSTOMER_MNGT_VISIBLE_ACCOUNT'
    ),
  canClientVisibleLocation: auth =>
    _get(auth, 'privileges', []).includes(
      'P408_CRM_CUSTOMER_MNGT_VISIBLE_LOCATION'
    ),
  canClientRemove: auth =>
    _get(auth, 'privileges', []).includes('P405_CRM_CUSTOMER_MNGT_REMOVE'),
  canClientAdvisorEdit: auth =>
    _get(auth, 'privileges', []).includes(
      'P404_CRM_CUSTOMER_MNGT_ADVISOR_EDIT'
    ),
  canCustomizations: auth =>
    _get(auth, 'privileges', []).includes('P601_CUSTOMIZATIONS'),
  canCustomizationConsent: auth =>
    _get(auth, 'privileges', []).includes('P602_CUSTOMIZATION_CONSENT'),
  canCustomizationAppLayout: auth =>
    _get(auth, 'privileges', []).includes('P603_CUSTOMIZATION_APP_LAYOUT'),
  canCustomizationRecommendation: auth =>
    _get(auth, 'privileges', []).includes(
      'P604_CUSTOMIZATION_RECOMMENDATION_RULE'
    ),
};

const checkPrivileges = (privilegesToCheck = [], privileges) =>
  privilegesToCheck.filter(privilege => {
    return (
      privileges.some(p => p.value === privilege) ||
      getAllSubOptions(privileges).includes(privilege)
    );
  });

const getAllSubOptions = privileges => {
  return privileges.reduce((acc, privilege) => {
    if (privilege.subOptLists) {
      privilege.subOptLists.forEach(subOptList => {
        acc.push(...subOptList.options.map(option => option.value));
      });
    }
    return acc;
  }, []);
};

const skinPrivileges = () => [
  {
    value: 'P103_SKU_LOOK_MNGT',
    label: i18n.t('c.l.sidebar.1130.skincarepro.title', 'Products'),
    desc: i18n.t(
      'p.s.role.privilege.products.desc',
      'Permissions to create, delete, and manage skincare products, routines, and treatments.'
    ),
    isShowSimulation: false,
    isShowV1: true,
  },
  {
    value: 'P108_RECOMMENDATION_RULE',
    label: i18n.t('c.l.sidebar.1140.skincarepro.title', 'Recommendation Rule'),
    desc: i18n.t(
      'p.s.role.privilege.recommendation.desc',
      'Permissions to create, delete, and manage recommendation rules.'
    ),
    isShowSimulation: false,
    isShowV1: true,
  },
  {
    value: 'P110_SURVEY_FORM',
    label: i18n.t('c.l.sidebar.1150.skincarepro.title', 'Survey Form'),
    desc: i18n.t(
      'p.s.role.privilege.surveyform.desc',
      'Permissions to create, delete, and manage survey forms.'
    ),
    isShowSimulation: true,
    isShowV1: false,
  },
  {
    value: 'P109_CUSTOMIZE_EMAIL',
    label: i18n.t('c.l.sidebar.1160.skincarepro.title', 'Email'),
    desc: i18n.t(
      'p.s.role.privilege.email.desc',
      'Permissions to customize email content.'
    ),
    isShowSimulation: false,
    isShowV1: true,
  },
  {
    value: 'P105_UI_CONFIGURATION',
    label: i18n.t('p.g.t.staffs.role.ui.configuration', 'UI Configuration'),
    desc: i18n.t(
      'p.s.role.privilege.ui.configuration.desc',
      'Permissions to configure UI display on App.'
    ),
    isShowSimulation: true,
    isShowV1: true,
  },
  {
    value: 'P402_CRM_CUSTOMER_MNGT',
    label: i18n.t('c.l.sidebar.7000.title', 'Client'),
    desc: i18n.t(
      'p.s.role.privilege.client.desc',
      'Permissions to view and search clients, access report histories, share client reports, and create or edit client profiles.'
    ),
    isShowSimulation: false,
    isShowV1: false,
    subOptLists: [
      {
        type: 'checkbox',
        options: [
          {
            value: 'P404_CRM_CUSTOMER_MNGT_ADVISOR_EDIT',
            label: i18n.t(
              'p.g.r.role.client.advisor.edit',
              'Edit client’s responsible Skincare Advisor'
            ),
          },
          {
            value: 'P405_CRM_CUSTOMER_MNGT_REMOVE',
            label: i18n.t(
              'p.g.r.role.client.remove.report',
              'Delete clients / delete client reports'
            ),
          },
        ],
      },
      {
        type: 'radio',
        label: i18n.t(
          'p.g.r.role.client.visible.advanced',
          'Advanced Settings - Client Data Visibility'
        ),
        options: [
          {
            value: 'P406_CRM_CUSTOMER_MNGT_VISIBLE_FULL',
            label: i18n.t(
              'p.g.r.role.client.visible.full',
              'Full access to all Locations & Skincare Advisors'
            ),
            isDefault: true,
          },
          {
            value: 'P407_CRM_CUSTOMER_MNGT_VISIBLE_ACCOUNT',
            label: i18n.t(
              'p.g.r.role.client.visible.account',
              'Limited to responsible Clients only'
            ),
            isDefault: false,
          },
          {
            value: 'P408_CRM_CUSTOMER_MNGT_VISIBLE_LOCATION',
            label: i18n.t(
              'p.g.r.role.client.visible.location',
              'Limited to assigned Locations only'
            ),
            isDefault: false,
          },
        ],
      },
    ],
  },
  {
    value: 'P102_WM_REPORT',
    label: i18n.t('p.g.t.staffs.role.analytics', 'Analytics'),
    desc: i18n.t(
      'p.s.role.privilege.report.desc',
      'Permissions to view and query data.'
    ),
    isShowSimulation: true,
    isShowV1: true,
  },
  {
    value: 'P403_CRM_STORE_MNGT',
    label: i18n.t('c.l.sidebar.9170.title', 'Location'),
    desc: i18n.t(
      'p.s.role.privilege.location.desc',
      'Permissions to edit, delete, and manage service location info.'
    ),
    isShowSimulation: true,
    isShowV1: false,
  },
  {
    value: 'P106_DEVICE',
    label: i18n.t('p.home.dashboard.license', 'License'),
    desc: i18n.t(
      'p.s.role.privilege.location.desc',
      'Permissions to view License usage and logout unused devices.'
    ),
    isShowSimulation: true,
    isShowV1: true,
  },
  {
    value: 'P501_SAAS_API',
    label: i18n.t('c.l.sidebar.11000.title', 'Server to Server API'),
    desc: i18n.t(
      'p.s.role.privilege.saas.desc',
      'Permissions to generate, delete, and manage API keys.'
    ),
    isShowSimulation: true,
    isShowV1: false,
    isEnterpriseOnly: true,
  },
  {
    value: 'P302_CX_TEAM_SETTINGS',
    label: i18n.t('c.l.sidebar.9130.title', 'Team'),
    desc: i18n.t(
      'p.s.role.privilege.team.desc',
      'Permissions to view and add team member accounts.'
    ),
    isShowSimulation: true,
    isShowV1: true,
    subOptLists: [
      {
        type: 'checkbox',
        options: [
          {
            value: 'P305_CX_TEAM_SETTINGS_EDIT',
            label: i18n.t('p.g.r.role.team.edit', 'Edit team member accounts'),
          },
          {
            value: 'P306_CX_TEAM_SETTINGS_DELETE',
            label: i18n.t(
              'p.g.r.role.team.delete',
              'Delete team member accounts'
            ),
          },
          {
            value: 'P307_CX_TEAM_SETTINGS_ROLE_MNGT',
            label: i18n.t(
              'p.g.r.role.team.role.management',
              'Access Role Management to add, edit, duplicate and delete roles'
            ),
          },
        ],
      },
    ],
  },
];

export {privilegeUtils, skinPrivileges, checkPrivileges};
